import type { FlightServerCuts, FlightServerOrder } from '../results/filter_logic.js';
import type { FlightFilterIntent } from '../results/filter_intent_source.js';
import type { FlightCabinPolicy } from '../results/filters/cabin_policy.js';
import type { FlightFilterFacets } from '../results/filters/flight_filter_panel.js';
import type { FlightOfferSource } from '../results/flight_offer_source.js';

/*
 * O contrato de busca do explorer, declarado pela FORMA (#4997): o kit não
 * importa o wire do chat nem o domain. O genui-kit prova num teste de tipo que
 * o `FlightExplorerOffer` e as facetas do domain cabem aqui.
 */

/** O que o explorer lê de uma oferta: a oferta dos resultados mais a classe
 *  tarifária, que o pedido offline leva junto da ida escolhida. */
export interface FlightExplorerOffer extends FlightOfferSource {
  fareClass?: string | undefined;
}

/** O que o explorer lê das facetas do refine: as do painel, o total do conjunto
 *  (o rodapé da contagem) e as companhias (o nome no chip do filtro por texto). */
export type FlightSearchFacets = FlightFilterFacets & {
  total?: number | undefined;
  airlines: readonly { code: string; name: string }[];
};

/**
 * Parâmetros da busca determinística de voos (explorer → shell → BFF
 * `POST /flight/search`). Espelha o `FlightSearchBodySchema` do app-integration:
 * rota IATA + datas ISO; `returnDate` presente ⇒ round-trip (o passo 1 JÁ leva a
 * volta — o searchKey nasce round-trip). `policyTravelerId` é o SUJEITO da
 * política (viajante), distinto do requester (JWT do shell).
 */
export interface FlightSearchParams {
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  cabin?: 'economy' | 'premium' | 'business' | 'first';
  policyTravelerId?: string;
  /**
   * DICA de reuso do `searchKey` (spec flight-explorer-search-reuse): a ref
   * opaca de UMA oferta já em mãos da MESMA busca — a semente do card, ou, no
   * card de ida e volta (que não semeia), qualquer par dele (#5793). Qualquer
   * ref serve (é o handle da sessão do agente). O servidor tenta a busca no
   * cache do provider com o key dessa sessão e cai na busca nova quando não dá.
   * Só a busca de MOUNT manda; `Buscar` da faixa, cabine e retry re-formam o
   * pedido e saem sem — preço fresco por decisão.
   */
  anchorOfferId?: string;
  /**
   * LINHAGEM do roteiro: de que card multi (`flight_multi_offers`) e de que
   * perna esta busca de UM trecho nasceu. `challengeId` é o do card — o mesmo
   * que ele ecoa como `interactionId` ao despachar `select_multi_leg_fares` —
   * e `legIndex` a perna buscada. Serve ao servidor pra amarrar a sessão de
   * busca da perna ao roteiro que a originou (adoção do trecho no card), em vez
   * de tratá-la como uma viagem de ida avulsa. Só o explorer aberto de um card
   * multi manda; a bancada e o card binário saem sem.
   */
  itinerary?: { challengeId: string; legIndex: number };
}

/** Uma página do conjunto no fornecedor — 1-based, mesmo contrato do provider. */
export interface FlightPageRequest {
  number: number;
  limit: number;
}

/** Onde a página caiu: o que o scroll precisa pra saber se continua. `total` é a
 *  contagem do conjunto filtrado inteiro (no refine ela também vem em facets). */
export interface FlightPageInfo {
  number: number;
  hasNext: boolean;
  total?: number;
}

/** A página que o explorer pede por vez. Igual ao limit que o refine sempre usou —
 *  a página 1 continua idêntica à de hoje. Teto do fio: MODAL_SESSION_FLIGHT_PAGE_LIMIT_MAX. */
export const EXPLORER_PAGE_LIMIT = 100;

/**
 * Por que a busca do seam falhou (#2926). O explorer não conhece o transporte do
 * host, então a causa viaja NO ERRO: a borda de dados marca `searchFailure` e o
 * kit só lê. Serve à telemetria do fracasso (hoje `search_failed` não deixava
 * rastro nenhum) e mantém a distinção que o diagnóstico do bug exigiu — rede
 * caída na janela de 40s não é a mesma coisa que fornecedor fora do ar.
 * `timeout` é o prazo estourado no servidor (504, #4465): a tela tem desfecho
 * próprio para ele, porque repetir a mesma busca tende a estourar de novo.
 * `policy_evaluation` é a política do viajante que não pôde ser avaliada (422,
 * #5376): terminal — não há busca sem o viajante, e a tela manda falar com o
 * administrador da empresa em vez de oferecer "tentar de novo".
 * `search_expired` é a busca que venceu no cache do fornecedor (409, #5588): o
 * pedido sobre ela volta vazio para sempre, então "tentar de novo" refaz a busca
 * inteira com os mesmos critérios em vez de repetir o pedido.
 */
export type FlightSearchFailureCause =
  | 'network'
  | 'http'
  | 'timeout'
  | 'policy_evaluation'
  | 'search_expired'
  | 'parse'
  | 'unknown';

/** As falhas de busca que a página de erro trata à parte da genérica: o prazo
 *  estourado (#4465), a política do viajante que não avaliou (#5376) e a busca
 *  que venceu no fornecedor (#5588). */
export type LegFailure = Extract<
  FlightSearchFailureCause,
  'timeout' | 'policy_evaluation' | 'search_expired'
>;

const TAGGED_CAUSES = new Set<string>([
  'network',
  'http',
  'timeout',
  'policy_evaluation',
  'search_expired',
  'parse',
]);

/**
 * A causa que o erro carrega, com degrade honesto: sem marca, o `name` ainda
 * separa o que TODO runtime nomeia igual (`AbortError` de aba que hiberna,
 * `TypeError` do fetch que não sai, `ZodError` do corpo fora do contrato); o
 * resto é `unknown` — inventar causa aqui seria pior que admitir a ignorância.
 */
export function flightSearchFailureCause(error: unknown): FlightSearchFailureCause {
  if (typeof error !== 'object' || error === null) return 'unknown';
  const tagged = (error as { searchFailure?: unknown }).searchFailure;
  if (typeof tagged === 'string' && TAGGED_CAUSES.has(tagged)) {
    return tagged as FlightSearchFailureCause;
  }
  const name = (error as { name?: unknown }).name;
  if (name === 'AbortError' || name === 'TypeError') return 'network';
  if (name === 'ZodError') return 'parse';
  return 'unknown';
}

/** Resposta das duas buscas — ofertas já no shape do fio (mapper do BFF). */
export interface FlightSearchResult {
  offers: FlightExplorerOffer[];
  page?: FlightPageInfo;
  /** As cabines que a política do viajante permite (#6438) — só na busca, nunca no
   *  return-search. Ausente = política não lida: o seletor não marca nada. */
  cabinPolicy?: FlightCabinPolicy | undefined;
}

/**
 * Seam de busca server-side do FlightExplorer, injetado pelo shell via
 * `GenUiConfig.flightSearch` (mesmo padrão do `rentalRefine`: leitura
 * determinística, sem LLM) OU por prop (`FlightExplorerProps.search` — o host
 * de página da bancada). `returnSearch` recebe a ref OPACA da IDA escolhida
 * (o mesmo `offerId` que o card usa em /flight/tariff) — voltas pareadas na
 * MESMA sessão. Ausente ⇒ os hosts degradam pro `FlightFilterDrawer` legado.
 */
export interface FlightSearchFn {
  search: (params: FlightSearchParams) => Promise<FlightSearchResult>;
  returnSearch: (outboundOfferId: string, page?: FlightPageRequest) => Promise<FlightSearchResult>;
  /**
   * Cortes E ORDENAÇÃO aplicados NO SERVIDOR sobre a busca corrente (`POST
   * /flight/refine-search`): `anchorOfferId` é qualquer ref opaca da lista em cena
   * — é ela que identifica a sessão e, dentro dela, o `searchKey`. Devolve a página
   * filtrada + as facetas (contagem e "a partir de" por opção, companhias do
   * conjunto, faixa de preço da busca inteira).
   *
   * Opcional: sem ele o painel volta a cortar e contar sobre a lista em mão.
   */
  refine?: (
    anchorOfferId: string,
    request: { cuts: FlightServerCuts; order?: FlightServerOrder; page?: FlightPageRequest },
  ) => Promise<{
    offers: FlightExplorerOffer[];
    facets: FlightSearchFacets;
    page?: FlightPageInfo;
  }>;
  /**
   * Filtro por TEXTO (spec flight-text-filter-chips): o texto do composer "Filtre do seu
   * jeito" vira chips com PATCH sobre o estado do painel. `anchorOfferId` identifica a sessão
   * como no `refine`; o juiz do modal-session só LÊ a sessão — quem refina continua sendo o
   * explorer, pelo mesmo `refine`. Opcional: sem ele o composer não aparece.
   */
  interpretFilter?: (
    anchorOfferId: string,
    request: { text: string; locale: string; legs?: string[] },
  ) => Promise<FlightFilterIntent>;
}

/**
 * O pedido normalizado POR PERNAS do FlightExplorer — a MESMA superfície no
 * drawer do chat (montado do echo `search` do card, grupo F) e na bancada
 * (montado do `BookingRequest` do web-chat, grupo J). `round_trip` = `legs[0]`
 * + `returnDate`; `multi_leg` = N pernas one-way (fan-out de uma busca por
 * perna no mount).
 */
export interface FlightExplorerRequest {
  mode: 'one_way' | 'round_trip' | 'multi_leg';
  legs: { origin: string; destination: string; departDate: string }[];
  returnDate?: string;
  cabin?: 'economy' | 'premium' | 'business' | 'first';
  policyTravelerId?: string;
  /**
   * Quantas pessoas VIAJAM — o subtítulo da tela de tarifas (#2262) as conta.
   *
   * Não é eixo de busca e não entra em `legSearchParams`: o corpo do
   * `POST /flight/search` não tem `travelers` nem `passengers` (ver
   * `travelers.policyAria` no i18n) — multiplicidade se resolve na emissão, em N
   * vouchers. Quem sabe o número é o host do CHECKOUT: a bancada o tem
   * (`BookingRequest.travelers`), a casca do chat não (o eco `search` do card não
   * carrega contagem), e ali o subtítulo simplesmente não diz quantos.
   */
  travelerCount?: number;
  /**
   * O `challengeId` do card multi de onde o explorer foi aberto — a LINHAGEM
   * que cada busca de perna leva em `FlightSearchParams.itinerary`. É do
   * PEDIDO, não da geometria (`tripGeometryOf` não o lê): re-editar a rota na
   * faixa ou trocar a cabine ainda é o mesmo card, então ele sobrevive a toda
   * re-forma do pedido. Ausente fora do card multi.
   */
  itineraryChallengeId?: string;
}
